import { WAF_REPORT_IGNORED_USER_AGENTS } from './errors/airbrakeConfig';
import { isIgnoredUserAgent } from './errors/gate';
import { reportError } from './errors/reportError';

type AwsWafIntegration = {
  getToken(): Promise<string>;
  hasToken?(): boolean;
  fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
};

declare global {
  interface Window {
    AwsWafIntegration?: AwsWafIntegration;
  }
}

const reported = new Set<string>();

function pathOf(input: RequestInfo | URL): string {
  const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

  try {
    return new URL(raw, window.location.href).pathname;
  } catch {
    return String(raw).split('?')[0];
  }
}

function sdkTagPresent(): boolean {
  return document.querySelector('script[src*="awswaf.com"]') !== null;
}

function isAbort(error: unknown, init: RequestInit | undefined): boolean {
  return (
    (typeof error === 'object' && error !== null && (error as { name?: string }).name === 'AbortError') ||
    init?.signal?.aborted === true
  );
}

function reportOnce(kind: string, input: RequestInfo | URL, waf: AwsWafIntegration | undefined, error?: unknown): void {
  if (isIgnoredUserAgent(window.navigator?.userAgent, WAF_REPORT_IGNORED_USER_AGENTS)) {
    return;
  }

  const path = pathOf(input);
  const key = `${kind} ${path}`;

  if (reported.has(key)) {
    return;
  }

  reported.add(key);

  let hasToken: boolean | undefined;

  try {
    hasToken = waf && waf.hasToken ? waf.hasToken() : undefined;
  } catch {
    hasToken = undefined;
  }

  reportError(error ?? new Error(`wafFetch: ${kind}`), {
    source: 'wafFetch',
    kind,
    path,
    sdkTagPresent: sdkTagPresent(),
    hasToken,
  });
}

/**
 * Use this for requests to WAF-challenged paths: the catalog JSON under
 * /services_api/storefront/v1/products|categories|third_party and legacy catalog pages
 * (product.php, section.php, search.php). It waits for the challenge token when the SDK is
 * loaded; other same-origin calls may use it too. Do not patch window.fetch: the SDK's own
 * fetch wraps the global one and would recurse. The native fallback replays `init` as-is, so
 * it is only safe for GET/idempotent requests without a single-use body; non-idempotent
 * callers should not rely on wafFetch's fallback as-is.
 */
export async function wafFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (typeof window === 'undefined') {
    return fetch(input, init);
  }

  const waf = window.AwsWafIntegration;

  if (!waf) {
    if (sdkTagPresent()) {
      reportOnce('sdk_missing', input, undefined);
    }

    return fetch(input, init);
  }

  let response: Response;

  try {
    response = await waf.fetch(input, init);
  } catch (error) {
    if (isAbort(error, init)) {
      throw error;
    }

    const native = await fetch(input, init);
    reportOnce('token_failed', input, waf, error);

    return native;
  }

  if (response.status === 202 && response.headers.get('x-amzn-waf-action') === 'challenge') {
    reportOnce('challenged', input, waf);
  }

  return response;
}
